<script setup>
import { h, ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import TextArea from 'next/textarea/TextArea.vue';
import Switch from 'next/switch/Switch.vue';
import DurationInput from 'next/input/DurationInput.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const { currentAccount, accountId } = useAccount();
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const labels = useMapGetter('labels/getLabels');

const useAccountDefaults = ref(true);
const isEnabled = ref(false);
const duration = ref(null);
const unit = ref(DURATION_UNITS.MINUTES);
const message = ref('');
const warningBefore = ref(null);
const warningUnit = ref(DURATION_UNITS.MINUTES);
const warningMessage = ref('');
const ignoreWaiting = ref(false);
const labelToApply = ref({});

const labelOptions = computed(() =>
  labels.value?.length
    ? labels.value.map(label => ({
        id: label.title,
        name: label.title,
        icon: h('span', {
          class:
            'size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm',
          style: { backgroundColor: label.color },
        }),
      }))
    : []
);

const selectedLabelName = computed(() => labelToApply.value?.name ?? null);

const showForm = computed(() =>
  isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.AUTO_RESOLVE_CONVERSATIONS
  )
);

const unitForMinutes = minutes => {
  if (!minutes) return DURATION_UNITS.MINUTES;
  if (minutes % (24 * 60) === 0) return DURATION_UNITS.DAYS;
  if (minutes % 60 === 0) return DURATION_UNITS.HOURS;
  return DURATION_UNITS.MINUTES;
};

const fillFrom = source => {
  duration.value = source?.auto_resolve_after || null;
  message.value = source?.auto_resolve_message || '';
  ignoreWaiting.value = !!source?.auto_resolve_ignore_waiting;
  warningBefore.value = source?.auto_resolve_warning_before || null;
  warningMessage.value = source?.auto_resolve_warning_message || '';
  labelToApply.value =
    labelOptions.value.find(
      option => option.name === source?.auto_resolve_label
    ) || {};
  unit.value = unitForMinutes(duration.value);
  warningUnit.value = unitForMinutes(warningBefore.value);
  isEnabled.value = !!duration.value;
};

watch(
  () => [props.inbox?.id, props.inbox?.auto_resolve_config],
  () => {
    const config = props.inbox?.auto_resolve_config || {};
    useAccountDefaults.value = config.use_account !== false;
    if (!useAccountDefaults.value) fillFrom(config);
  },
  { immediate: true, deep: true }
);

watch(labelOptions, () => {
  if (useAccountDefaults.value) return;
  const name =
    labelToApply.value?.name ||
    props.inbox?.auto_resolve_config?.auto_resolve_label;
  if (!name) return;
  const match = labelOptions.value.find(option => option.name === name);
  if (match) labelToApply.value = match;
});

const onUseAccountChange = () => {
  if (!useAccountDefaults.value) fillFrom(currentAccount.value?.settings || {});
};

const buildConfig = () => {
  if (!showForm.value) return null;

  if (useAccountDefaults.value) {
    return { use_account: true };
  }

  if (!isEnabled.value) {
    return {
      use_account: false,
      auto_resolve_after: null,
      auto_resolve_message: '',
      auto_resolve_ignore_waiting: false,
      auto_resolve_label: null,
      auto_resolve_warning_before: null,
      auto_resolve_warning_message: '',
    };
  }

  if (duration.value < 10) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.ERROR'));
    return false;
  }

  const warningMinutes = warningBefore.value || null;
  if (warningMinutes) {
    if (warningMinutes < 10 || warningMinutes >= duration.value) {
      useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.WARNING.ERROR'));
      return false;
    }
    if (!warningMessage.value?.trim()) {
      useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.WARNING.MESSAGE.ERROR'));
      return false;
    }
  }

  return {
    use_account: false,
    auto_resolve_after: duration.value,
    auto_resolve_message: message.value,
    auto_resolve_ignore_waiting: ignoreWaiting.value,
    auto_resolve_label: selectedLabelName.value,
    auto_resolve_warning_before: warningMinutes,
    auto_resolve_warning_message: warningMinutes ? warningMessage.value : '',
  };
};

defineExpose({ buildConfig });
</script>

<template>
  <div>
    <div
      v-if="showForm"
      class="flex flex-col gap-4 pt-6 mt-2 border-t border-n-weak"
    >
      <div>
        <h3 class="text-heading-3 text-n-slate-12">
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.TITLE') }}
        </h3>
        <p class="mb-0 text-body-para text-n-slate-11">
          {{ t('INBOX_MGMT.AUTO_RESOLVE.USE_ACCOUNT_HELP') }}
        </p>
      </div>
      <div class="flex items-center justify-between">
        <span class="text-sm text-n-slate-12">
          {{ t('INBOX_MGMT.AUTO_RESOLVE.USE_ACCOUNT') }}
        </span>
        <Switch v-model="useAccountDefaults" @change="onUseAccountChange" />
      </div>

      <template v-if="!useAccountDefaults">
        <div class="flex items-center justify-between">
          <span class="text-sm text-n-slate-12">
            {{ t('INBOX_MGMT.AUTO_RESOLVE.ENABLE_INBOX') }}
          </span>
          <Switch v-model="isEnabled" />
        </div>
        <template v-if="isEnabled">
          <WithLabel
            :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.LABEL')"
            :help-message="
              t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.HELP')
            "
          >
            <div class="gap-2 w-full grid grid-cols-[3fr_1fr]">
              <DurationInput
                v-model="duration"
                v-model:unit="unit"
                min="0"
                max="1438560"
                class="w-full"
              />
            </div>
          </WithLabel>
          <WithLabel
            :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.LABEL')"
            :help-message="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.HELP')"
          >
            <TextArea
              v-model="message"
              auto-height
              class="w-full"
              :placeholder="
                t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.PLACEHOLDER')
              "
            />
          </WithLabel>
          <WithLabel
            :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.WARNING.LABEL')"
            :help-message="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.WARNING.HELP')"
          >
            <div class="gap-2 w-full grid grid-cols-[3fr_1fr]">
              <DurationInput
                v-model="warningBefore"
                v-model:unit="warningUnit"
                min="0"
                max="1439855"
                class="w-full"
              />
            </div>
          </WithLabel>
          <WithLabel
            :label="
              t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.WARNING.MESSAGE.LABEL')
            "
            :help-message="
              t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.WARNING.MESSAGE.HELP')
            "
          >
            <TextArea
              v-model="warningMessage"
              auto-height
              class="w-full"
              :placeholder="
                t(
                  'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.WARNING.MESSAGE.PLACEHOLDER'
                )
              "
            />
          </WithLabel>
          <div class="flex items-center justify-between">
            <span class="text-sm text-n-slate-12">
              {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.IGNORE_WAITING.LABEL') }}
            </span>
            <Switch v-model="ignoreWaiting" />
          </div>
          <WithLabel
            :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.LABEL')"
          >
            <SingleSelect
              v-model="labelToApply"
              :options="labelOptions"
              :placeholder="
                t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.PLACEHOLDER')
              "
              placeholder-icon="i-lucide-chevron-down"
              placeholder-trailing-icon
              variant="faded"
            />
          </WithLabel>
        </template>
      </template>
    </div>
  </div>
</template>
